/**
 * Mini Atomic CSS - 轻量原子化CSS引擎
 * 零依赖 | 开箱即用 | 不影响现有样式
 *
 * 使用方式：
 *   在 main.js 中 import './utils/atomic.js'
 *   模板中直接使用：<div class="w-[100px] bg-red hover:bg-[#eee] flex items-center">
 *
 * 支持自定义配置：
 *   window.__atomicConfig({ prefix: 'x-', colors: { ... } })
 */

(function () {
  "use strict";

  // ==================== 默认配置 ====================
  var DEFAULT_CONFIG = {
    prefix: "",

    separator: ":",

    colors: {
      transparent: "transparent",
      current: "currentColor",
      black: "#000",
      white: "#fff",
      gray: {
        50: "#f9fafb",
        100: "#f3f4f6",
        200: "#e5e7eb",
        300: "#d1d5db",
        400: "#9ca3af",
        500: "#6b7280",
        600: "#4b5563",
        700: "#374151",
        800: "#1f2937",
        900: "#111827",
      },
      red: {
        50: "#fef2f2",
        100: "#fee2e2",
        200: "#fecaca",
        300: "#fca5a5",
        400: "#f87171",
        500: "#ef4444",
        600: "#dc2626",
        700: "#b91c1c",
        800: "#991b1b",
        900: "#7f1d1d",
      },
      blue: {
        50: "#eff6ff",
        100: "#dbeafe",
        200: "#bfdbfe",
        300: "#93c5fd",
        400: "#60a5fa",
        500: "#3b82f6",
        600: "#2563eb",
        700: "#1d4ed8",
        800: "#1e40af",
        900: "#1e3a8a",
      },
      green: {
        50: "#f0fdf4",
        100: "#dcfce7",
        200: "#bbf7d0",
        300: "#86efac",
        400: "#4ade80",
        500: "#22c55e",
        600: "#16a34a",
        700: "#15803d",
        800: "#166534",
        900: "#14532d",
      },
      yellow: {
        50: "#fefce8",
        100: "#fef9c3",
        200: "#fef08a",
        300: "#fde047",
        400: "#facc15",
        500: "#eab308",
        600: "#ca8a04",
        700: "#a16207",
        800: "#854d0e",
        900: "#713f12",
      },
      orange: {
        50: "#fff7ed",
        100: "#ffedd5",
        200: "#fed7aa",
        300: "#fdba74",
        400: "#fb923c",
        500: "#f97316",
        600: "#ea580c",
        700: "#c2410c",
        800: "#9a3412",
        900: "#7c2d12",
      },
      pink: {
        50: "#fdf2f8",
        100: "#fce7f3",
        200: "#fbcfe8",
        300: "#f9a8d4",
        400: "#f472b6",
        500: "#ec4899",
        600: "#db2777",
        700: "#be185d",
        800: "#9d174d",
        900: "#831843",
      },
      purple: {
        50: "#faf5ff",
        100: "#f3e8ff",
        200: "#e9d5ff",
        300: "#d8b4fe",
        400: "#c084fc",
        500: "#a855f7",
        600: "#9333ea",
        700: "#7e22ce",
        800: "#6b21a8",
        900: "#581c87",
      },
      indigo: {
        50: "#eef2ff",
        100: "#e0e7ff",
        200: "#c7d2fe",
        300: "#a5b4fc",
        400: "#818cf8",
        500: "#6366f1",
        600: "#4f46e5",
        700: "#4338ca",
        800: "#3730a3",
        900: "#312e81",
      },
      teal: {
        50: "#f0fdfa",
        100: "#ccfbf1",
        200: "#99f6e4",
        300: "#5eead4",
        400: "#2dd4bf",
        500: "#14b8a6",
        600: "#0d9488",
        700: "#0f766e",
        800: "#115e59",
        900: "#134e4a",
      },
      cyan: {
        50: "#ecfeff",
        100: "#cffafe",
        200: "#a5f3fc",
        300: "#67e8f9",
        400: "#22d3ee",
        500: "#06b6d4",
        600: "#0891b2",
        700: "#0e7490",
        800: "#155e75",
        900: "#164e63",
      },
    },

    screens: {
      sm: "640px",
      md: "768px",
      lg: "1024px",
      xl: "1280px",
    },
  };

  // ==================== 全局状态 ====================
  var config = extend({}, DEFAULT_CONFIG);
  var cache = {};
  var styleEl = null;
  var sheet = null;
  var ruleIndex = 0;
  var observer = null;
  var buffer = "";

  // ==================== 工具函数 ====================
  function extend(target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      if (source) {
        for (var key in source) {
          if (Object.prototype.hasOwnProperty.call(source, key)) {
            var sv = source[key];
            var tv = target[key];
            if (
              typeof sv === "object" &&
              sv !== null &&
              !Array.isArray(sv) &&
              typeof tv === "object" &&
              tv !== null &&
              !Array.isArray(tv)
            ) {
              target[key] = extend(tv, sv);
            } else {
              target[key] = sv;
            }
          }
        }
      }
    }
    return target;
  }

  function escapeCSS(str) {
    if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
      try {
        return CSS.escape(str);
      } catch (_) {}
    }
    // 兜底：简单反斜杠前缀
    return str.replace(/[^a-zA-Z0-9\-_]/g, function (c) {
      return "\\" + c;
    });
  }

  function resolveColor(value) {
    if (!value) return null;
    if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(value))
      return value;
    if (/^rgb/.test(value)) return value;
    var parts = value.split("-");
    var base = config.colors[parts[0]];
    if (!base) return null;
    if (typeof base === "string") return base;
    if (parts.length === 1) return base[500] || null;
    if (parts.length >= 2) return base[parts[1]] || null;
    return null;
  }

  // ==================== 属性处理器 ====================
  var handlers = {
    rotate: function (v) {
      return (
        "transform: rotate(" + (isArbitrary(v) ? strip(v) : v + "deg") + ")"
      );
    },
    // 宽高
    w: function (v) {
      return isArbitrary(v) ? "width: " + strip(v) : "width: " + v + "px !important";
    },
    h: function (v) {
      return isArbitrary(v) ? "height: " + strip(v) : "height: " + v + "px";
    },
    "min-w": function (v) {
      return isArbitrary(v)
        ? "min-width: " + strip(v)
        : "min-width: " + v + "px";
    },
    "min-h": function (v) {
      return isArbitrary(v)
        ? "min-height: " + strip(v)
        : "min-height: " + v + "px";
    },
    "max-w": function (v) {
      return isArbitrary(v)
        ? "max-width: " + strip(v)
        : "max-width: " + v + "px";
    },
    "max-h": function (v) {
      return isArbitrary(v)
        ? "max-height: " + strip(v)
        : "max-height: " + v + "px";
    },

    // 间距
    p: function (v) {
      return isArbitrary(v) ? "padding: " + strip(v) : "padding: " + v + "px";
    },
    pt: function (v) {
      return isArbitrary(v)
        ? "padding-top: " + strip(v)
        : "padding-top: " + v + "px";
    },
    pr: function (v) {
      return isArbitrary(v)
        ? "padding-right: " + strip(v)
        : "padding-right: " + v + "px";
    },
    pb: function (v) {
      return isArbitrary(v)
        ? "padding-bottom: " + strip(v)
        : "padding-bottom: " + v + "px";
    },
    pl: function (v) {
      return isArbitrary(v)
        ? "padding-left: " + strip(v)
        : "padding-left: " + v + "px";
    },
    px: function (v) {
      return isArbitrary(v)
        ? "padding-left: " + strip(v) + "; padding-right: " + strip(v)
        : "padding-left: " + v + "px; padding-right: " + v + "px";
    },
    py: function (v) {
      return isArbitrary(v)
        ? "padding-top: " + strip(v) + "; padding-bottom: " + strip(v)
        : "padding-top: " + v + "px; padding-bottom: " + v + "px";
    },

    m: function (v) {
      return isArbitrary(v) ? "margin: " + strip(v) : "margin: " + v + "px";
    },
    mt: function (v) {
      return isArbitrary(v)
        ? "margin-top: " + strip(v)
        : "margin-top: " + v + "px";
    },
    mr: function (v) {
      return isArbitrary(v)
        ? "margin-right: " + strip(v)
        : "margin-right: " + v + "px";
    },
    mb: function (v) {
      return isArbitrary(v)
        ? "margin-bottom: " + strip(v)
        : "margin-bottom: " + v + "px";
    },
    ml: function (v) {
      return isArbitrary(v)
        ? "margin-left: " + strip(v)
        : "margin-left: " + v + "px";
    },
    mx: function (v) {
      return isArbitrary(v)
        ? "margin-left: " + strip(v) + "; margin-right: " + strip(v)
        : "margin-left: " + v + "px; margin-right: " + v + "px";
    },
    my: function (v) {
      return isArbitrary(v)
        ? "margin-top: " + strip(v) + "; margin-bottom: " + strip(v)
        : "margin-top: " + v + "px; margin-bottom: " + v + "px";
    },

    // 定位
    top: function (v) {
      return isArbitrary(v) ? "top: " + strip(v) : "top: " + v + "px";
    },
    right: function (v) {
      return isArbitrary(v) ? "right: " + strip(v) : "right: " + v + "px";
    },
    bottom: function (v) {
      return isArbitrary(v) ? "bottom: " + strip(v) : "bottom: " + v + "px";
    },
    left: function (v) {
      return isArbitrary(v) ? "left: " + strip(v) : "left: " + v + "px";
    },
    inset: function (v) {
      return isArbitrary(v)
        ? "top: " +
            strip(v) +
            "; right: " +
            strip(v) +
            "; bottom: " +
            strip(v) +
            "; left: " +
            strip(v)
        : "top: " +
            v +
            "px; right: " +
            v +
            "px; bottom: " +
            v +
            "px; left: " +
            v +
            "px";
    },

    // 圆角
    rounded: function (v) {
      return isArbitrary(v)
        ? "border-radius: " + strip(v)
        : "border-radius: " + v + "px";
    },
    "rounded-t": function (v) {
      var val = isArbitrary(v) ? strip(v) : v + "px";
      return (
        "border-top-left-radius: " + val + "; border-top-right-radius: " + val
      );
    },
    "rounded-b": function (v) {
      var val = isArbitrary(v) ? strip(v) : v + "px";
      return (
        "border-bottom-left-radius: " +
        val +
        "; border-bottom-right-radius: " +
        val
      );
    },
    "rounded-l": function (v) {
      var val = isArbitrary(v) ? strip(v) : v + "px";
      return (
        "border-top-left-radius: " + val + "; border-bottom-left-radius: " + val
      );
    },
    "rounded-r": function (v) {
      var val = isArbitrary(v) ? strip(v) : v + "px";
      return (
        "border-top-right-radius: " +
        val +
        "; border-bottom-right-radius: " +
        val
      );
    },

    // 文字
    text: function (v) {
      if (isArbitrary(v)) return "color: " + strip(v);
      var color = resolveColor(v);
      if (color) return "color: " + color;
      if (/^\d+$/.test(v)) return "font-size: " + v + "px";
      return null;
    },
    color: function (v) {
      if (isArbitrary(v)) return "color: " + strip(v);
      var color = resolveColor(v);
      if (color) return "color: " + color;
      return null;
    },

    // 字体粗细
    font: function (v) {
      var map = {
        thin: "100",
        extralight: "200",
        light: "300",
        normal: "400",
        medium: "500",
        semibold: "600",
        bold: "700",
        extrabold: "800",
        black: "900",
      };
      return "font-weight: " + (map[v] || v);
    },

    // 背景
    bg: function (v) {
      if (isArbitrary(v)) {
        var raw = strip(v);
        if (
          /^(linear-gradient|radial-gradient|conic-gradient|repeating-linear-gradient|repeating-radial-gradient|url)\s*\(/i.test(
            raw,
          )
        ) {
          return "background-image: " + raw;
        }
        return "background-color: " + raw;
      }
      var color = resolveColor(v);
      if (color) return "background-color: " + color;
      return null;
    },
    "bg-image": function (v) {
      return isArbitrary(v)
        ? "background-image: " + strip(v)
        : "background-image: " + v;
    },
    "bg-gradient": function (v) {
      return isArbitrary(v)
        ? "background-image: " + strip(v)
        : "background-image: " + v;
    },

    // 渐变 — CSS 变量桥接模式：bg-linear-* + from-* + to-* + via-*
    "bg-linear": function (v) {
      var dirMap = {
        "to-r": "to right",
        "to-l": "to left",
        "to-t": "to top",
        "to-b": "to bottom",
        "to-tr": "to top right",
        "to-tl": "to top left",
        "to-br": "to bottom right",
        "to-bl": "to bottom left",
      };
      var dir = dirMap[v] || (v ? (isArbitrary(v) ? strip(v) : v + "deg") : "to bottom");
      return "background-image: linear-gradient(" + dir + ", var(--tw-gradient-from, transparent), var(--tw-gradient-to, transparent))";
    },
    from: function (v) {
      var color = isArbitrary(v) ? strip(v) : (resolveColor(v) || "transparent");
      return "--tw-gradient-from: " + color + "; --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, transparent)";
    },
    to: function (v) {
      var color = isArbitrary(v) ? strip(v) : (resolveColor(v) || "transparent");
      return "--tw-gradient-to: " + color;
    },
    via: function (v) {
      var color = isArbitrary(v) ? strip(v) : (resolveColor(v) || "transparent");
      return "--tw-gradient-stops: var(--tw-gradient-from), " + color + ", var(--tw-gradient-to, transparent)";
    },

    // 边框
    border: function (v) {
      if (!v) return "border: 1px solid #e5e7eb";
      var color = resolveColor(v);
      if (color) return "border-color: " + color;
      if (isArbitrary(v)) return "border: " + strip(v);
      return "border: 1px solid #e5e7eb";
    },
    "border-t": function (v) {
      return (
        "border-top: 1px solid " +
        (v ? resolveColor(v) || "#e5e7eb" : "#e5e7eb")
      );
    },
    "border-r": function (v) {
      return (
        "border-right: 1px solid " +
        (v ? resolveColor(v) || "#e5e7eb" : "#e5e7eb")
      );
    },
    "border-b": function (v) {
      return (
        "border-bottom: 1px solid " +
        (v ? resolveColor(v) || "#e5e7eb" : "#e5e7eb")
      );
    },
    "border-l": function (v) {
      return (
        "border-left: 1px solid " +
        (v ? resolveColor(v) || "#e5e7eb" : "#e5e7eb")
      );
    },

    // 透明度
    opacity: function (v) {
      return "opacity: " + parseInt(v) / 100;
    },

    // z-index
    z: function (v) {
      return "z-index: " + v;
    },

    // gap
    gap: function (v) {
      return isArbitrary(v) ? "gap: " + strip(v) : "gap: " + v + "px";
    },
    "gap-x": function (v) {
      return isArbitrary(v)
        ? "column-gap: " + strip(v)
        : "column-gap: " + v + "px";
    },
    "gap-y": function (v) {
      return isArbitrary(v) ? "row-gap: " + strip(v) : "row-gap: " + v + "px";
    },

    // cursor
    cursor: function (v) {
      return "cursor: " + v;
    },

    // overflow
    overflow: function (v) {
      return "overflow: " + v;
    },
    "overflow-x": function (v) {
      return "overflow-x: " + v;
    },
    "overflow-y": function (v) {
      return "overflow-y: " + v;
    },

    // 过渡
    transition: function (v) {
      return "transition: " + (v || "all 0.2s ease");
    },
    duration: function (v) {
      return "transition-duration: " + v + "ms";
    },

    // 阴影
    shadow: function (v) {
      if (strip(v)) return "box-shadow: " + strip(v);
      var map = {
        sm: "0 1px 2px rgba(0,0,0,0.05)",
        DEFAULT: "0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)",
        md: "0 4px 6px rgba(0,0,0,0.07), 0 2px 4px rgba(0,0,0,0.06)",
        lg: "0 10px 15px rgba(0,0,0,0.1), 0 4px 6px rgba(0,0,0,0.05)",
        xl: "0 20px 25px rgba(0,0,0,0.1), 0 10px 10px rgba(0,0,0,0.04)",
        none: "none",
      };
      return "box-shadow: " + (map[v] || v);
    },

    // 文本对齐
    "text-align": function (v) {
      return "text-align: " + v;
    },

    // 行高
    leading: function (v) {
      return "line-height: " + (isArbitrary(v) ? strip(v) : v + "px");
    },

    // 字间距
    tracking: function (v) {
      return "letter-spacing: " + (isArbitrary(v) ? strip(v) : v + "px");
    },
  };

  // 静态类（不需要值的）
  var staticClasses = {
    flex: "display: flex",
    "inline-flex": "display: inline-flex",
    block: "display: block",
    "inline-block": "display: inline-block",
    inline: "display: inline",
    hidden: "display: none",
    grid: "display: grid",
    "inline-grid": "display: inline-grid",
    relative: "position: relative",
    absolute: "position: absolute",
    fixed: "position: fixed",
    sticky: "position: sticky",
    "flex-col": "flex-direction: column",
    "flex-row": "flex-direction: row",
    "flex-wrap": "flex-wrap: wrap",
    "flex-nowrap": "flex-wrap: nowrap",
    "flex-1": "flex: 1 1 0%",
    "flex-auto": "flex: 1 1 auto",
    "flex-none": "flex: none",
    "items-start": "align-items: flex-start",
    "items-center": "align-items: center",
    "items-end": "align-items: flex-end",
    "items-stretch": "align-items: stretch",
    "items-baseline": "align-items: baseline",
    "justify-start": "justify-content: flex-start",
    "justify-center": "justify-content: center",
    "justify-end": "justify-content: flex-end",
    "justify-between": "justify-content: space-between",
    "justify-around": "justify-content: space-around",
    "justify-evenly": "justify-content: space-evenly",
    "self-start": "align-self: flex-start",
    "self-center": "align-self: center",
    "self-end": "align-self: flex-end",
    "self-stretch": "align-self: stretch",
    "text-left": "text-align: left",
    "text-center": "text-align: center",
    "text-right": "text-align: right",
    "text-nowrap": "white-space: nowrap",
    "text-ellipsis":
      "text-overflow: ellipsis; overflow: hidden; white-space: nowrap",
    truncate: "overflow: hidden; text-overflow: ellipsis; white-space: nowrap",
    uppercase: "text-transform: uppercase",
    lowercase: "text-transform: lowercase",
    capitalize: "text-transform: capitalize",
    "cursor-pointer": "cursor: pointer",
    "cursor-not-allowed": "cursor: not-allowed",
    "cursor-default": "cursor: default",
    "cursor-move": "cursor: move",
    "overflow-auto": "overflow: auto",
    "overflow-hidden": "overflow: hidden",
    "overflow-scroll": "overflow: scroll",
    "overflow-visible": "overflow: visible",
    "select-none": "user-select: none",
    "select-all": "user-select: all",
    "select-text": "user-select: text",
    "pointer-events-none": "pointer-events: none",
    "pointer-events-auto": "pointer-events: auto",
    "w-full": "width: 100%",
    "w-screen": "width: 100vw",
    "w-auto": "width: auto",
    "h-full": "height: 100%",
    "h-screen": "height: 100vh",
    "h-auto": "height: auto",
    "min-w-0": "min-width: 0",
    "min-w-full": "min-width: 100%",
    "min-h-0": "min-height: 0",
    "min-h-full": "min-height: 100%",
    "max-w-full": "max-width: 100%",
    "max-h-full": "max-height: 100%",
    "rounded-full": "border-radius: 9999px",
    "rounded-none": "border-radius: 0",
    "border-none": "border: none",
    "border-solid": "border-style: solid",
    "border-dashed": "border-style: dashed",
    "border-dotted": "border-style: dotted",
    "shadow-none": "box-shadow: none",
    "m-auto": "margin: auto",
    "mx-auto": "margin-left: auto; margin-right: auto",
    "my-auto": "margin-top: auto; margin-bottom: auto",
    "mt-auto": "margin-top: auto",
    "mb-auto": "margin-bottom: auto",
    "ml-auto": "margin-left: auto",
    "mr-auto": "margin-right: auto",
    "p-0": "padding: 0",
    "m-0": "margin: 0",
    "opacity-0": "opacity: 0",
    "opacity-100": "opacity: 1",
    "z-auto": "z-index: auto",
    "no-underline": "text-decoration: none",
    underline: "text-decoration: underline",
    "line-through": "text-decoration: line-through",
    italic: "font-style: italic",
    "not-italic": "font-style: normal",
    antialiased:
      "-webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale",
    "box-border": "box-sizing: border-box",
    "box-content": "box-sizing: content-box",
    visible: "visibility: visible",
    invisible: "visibility: hidden",
    "list-none": "list-style-type: none",
    "whitespace-normal": "white-space: normal",
    "whitespace-nowrap": "white-space: nowrap",
    "whitespace-pre": "white-space: pre",
    "break-all": "word-break: break-all",
    "break-words": "word-wrap: break-word",
    "resize-none": "resize: none",
    resize: "resize: both",
    "sr-only":
      "position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0,0,0,0); white-space: nowrap; border-width: 0",
    "transition-none": "transition: none",
    "transition-all": "transition: all 0.2s ease",
    "duration-75": "transition-duration: 75ms",
    "duration-100": "transition-duration: 100ms",
    "duration-150": "transition-duration: 150ms",
    "duration-200": "transition-duration: 200ms",
    "duration-300": "transition-duration: 300ms",
    "duration-500": "transition-duration: 500ms",
    "duration-700": "transition-duration: 700ms",
    "duration-1000": "transition-duration: 1000ms",
    "ease-linear": "transition-timing-function: linear",
    "ease-in": "transition-timing-function: cubic-bezier(0.4,0,1,1)",
    "ease-out": "transition-timing-function: cubic-bezier(0,0,0.2,1)",
    "ease-in-out": "transition-timing-function: cubic-bezier(0.4,0,0.2,1)",
    "scale-0": "transform: scale(0)",
    "scale-50": "transform: scale(0.5)",
    "scale-75": "transform: scale(0.75)",
    "scale-90": "transform: scale(0.9)",
    "scale-95": "transform: scale(0.95)",
    "scale-100": "transform: scale(1)",
    "scale-105": "transform: scale(1.05)",
    "scale-110": "transform: scale(1.1)",
    "scale-125": "transform: scale(1.25)",
    "rotate-0": "transform: rotate(0deg)",
    "rotate-45": "transform: rotate(45deg)",
    "rotate-90": "transform: rotate(90deg)",
    "rotate-180": "transform: rotate(180deg)",
    "object-cover": "object-fit: cover",
    "object-contain": "object-fit: contain",
    "object-fill": "object-fit: fill",
    "object-none": "object-fit: none",
    "aspect-square": "aspect-ratio: 1/1",
    "aspect-video": "aspect-ratio: 16/9",
    "grid-cols-1": "grid-template-columns: repeat(1, minmax(0, 1fr))",
    "grid-cols-2": "grid-template-columns: repeat(2, minmax(0, 1fr))",
    "grid-cols-3": "grid-template-columns: repeat(3, minmax(0, 1fr))",
    "grid-cols-4": "grid-template-columns: repeat(4, minmax(0, 1fr))",
    "grid-cols-5": "grid-template-columns: repeat(5, minmax(0, 1fr))",
    "grid-cols-6": "grid-template-columns: repeat(6, minmax(0, 1fr))",
    "col-span-1": "grid-column: span 1 / span 1",
    "col-span-2": "grid-column: span 2 / span 2",
    "col-span-3": "grid-column: span 3 / span 3",
    "col-span-full": "grid-column: 1 / -1",
    "outline-none": "outline: none",
  };

  // ==================== 变体 ====================
  var variants = {
    hover: ":hover",
    focus: ":focus",
    active: ":active",
    visited: ":visited",
    "focus-within": ":focus-within",
    "focus-visible": ":focus-visible",
    disabled: "[disabled]",
    "first-child": ":first-child",
    "last-child": ":last-child",
    "nth-child-odd": ":nth-child(odd)",
    "nth-child-even": ":nth-child(even)",
    "not-first": ":not(:first-child)",
    "not-last": ":not(:last-child)",
    "only-child": ":only-child",
  };

  // 响应式变体（基于 screens 配置）
  function getResponsiveVariants() {
    var result = {};
    var screens = config.screens || {};
    for (var key in screens) {
      if (Object.prototype.hasOwnProperty.call(screens, key)) {
        result[key] = "@media (min-width: " + screens[key] + ")";
      }
    }
    return result;
  }

  // ==================== 解析器 ====================
  function isArbitrary(v) {
    return v && v.charAt(0) === "[" && v.charAt(v.length - 1) === "]";
  }

  function strip(v) {
    // 任意值中下划线 _ 还原为空格（与 Tailwind 行为一致）
    // 类名 token 不允许空格，书写时用 _ 代替，生成 CSS 时还原
    return v.slice(1, -1).replace(/_/g, " ");
  }

  // 方括号感知的类名拆分：[...] 内部的空格不作为分隔符
  function splitClassName(str) {
    var result = [];
    var buf = "";
    var depth = 0;
    for (var i = 0; i < str.length; i++) {
      var c = str.charAt(i);
      if (c === "[") depth++;
      else if (c === "]") depth = Math.max(0, depth - 1);
      if (/\s/.test(c) && depth === 0) {
        if (buf) {
          result.push(buf);
          buf = "";
        }
      } else {
        buf += c;
      }
    }
    if (buf) result.push(buf);
    return result;
  }

  function parseUtility(cls) {
    var sep = config.separator;

    // ---------- 1. 提取变体：尊重 [] 边界，不切 [] 内部的 : ----------
    var variantPart = "";
    var utilityPart = cls;
    var variantIdx = -1;
    (function () {
      var inB = 0;
      for (var s = 0; s < cls.length; s++) {
        var sc = cls.charAt(s);
        if (sc === "[") inB++;
        else if (sc === "]") inB = Math.max(0, inB - 1);
        else if (inB === 0 && sc === sep && s > 0) {
          variantIdx = s;
          return;
        }
      }
    })();
    if (variantIdx > 0) {
      var possibleVariant = cls.substring(0, variantIdx);
      var allVariants = extend({}, variants, getResponsiveVariants());
      if (allVariants[possibleVariant]) {
        variantPart = possibleVariant;
        utilityPart = cls.substring(variantIdx + 1);
      }
    }

    // ---------- 2. 切属性和值：尊重 [] 边界，不切 [] 内部的 - ----------
    //    并且优先匹配最长的已知属性名 (min-w、border-t 等)
    var dashIdx = -1;
    var bestLen = 0;
    (function () {
      var inB = 0;
      for (var d = 0; d < utilityPart.length; d++) {
        var dc = utilityPart.charAt(d);
        if (dc === "[") inB++;
        else if (dc === "]") inB = Math.max(0, inB - 1);
        else if (inB === 0 && dc === "-") {
          var cand = utilityPart.substring(0, d);
          if (handlers[cand] && cand.length > bestLen) {
            dashIdx = d;
            bestLen = cand.length;
            // 继续向后扫，尝试更长的匹配
          }
        }
      }
    })();
    if (dashIdx === -1) {
      return { variant: variantPart, utility: utilityPart, value: null };
    }

    var prop = utilityPart.substring(0, dashIdx);
    var value = utilityPart.substring(dashIdx + 1);
    return { variant: variantPart, utility: prop, value: value || null };
  }

  // ==================== CSS 生成 ====================
  function generateCSS(cls) {
    if (staticClasses[cls]) {
      return staticClasses[cls];
    }

    var parsed = parseUtility(cls);
    var prop = parsed.utility;
    var value = parsed.value;

    if (!value) {
      if (staticClasses[prop]) return staticClasses[prop];
      return null;
    }

    var handler = handlers[prop];
    if (handler) {
      return handler(value);
    }

    return null;
  }

  function buildRule(cls) {
    var prefix = config.prefix || "";
    var fullCls = prefix + cls;
    var css = generateCSS(cls);
    if (!css) return null;

    var parsed = parseUtility(cls);
    var variant = parsed.variant;

    if (!variant) {
      return "." + escapeCSS(fullCls) + " { " + css + " }";
    }

    var allVariants = extend({}, variants, getResponsiveVariants());
    var variantSelector = allVariants[variant];
    if (!variantSelector) return null;

    // 响应式变体
    if (variantSelector.charAt(0) === "@") {
      return (
        variantSelector + " { ." + escapeCSS(fullCls) + " { " + css + " } }"
      );
    }

    // 伪类变体
    return "." + escapeCSS(fullCls) + variantSelector + " { " + css + " }";
  }

  function insertRule(cls) {
    if (cache[cls]) return;

    var rule = buildRule(cls);
    if (!rule) return;

    cache[cls] = true;
    buffer += rule + "\n";

    try {
      if (styleEl) {
        styleEl.textContent = buffer;
      }
    } catch (e) {
      try {
        if (styleEl) {
          styleEl.innerHTML = buffer;
          console.warn("[atomic] textContent fallback to innerHTML for:", cls);
        }
      } catch (_) {
        console.error("[atomic] failed to inject rule:", cls, e);
      }
    }
  }

  // ==================== DOM 监听 ====================
  function processElement(el) {
    if (!el || !el.className) return;
    var classes = el.className;
    if (typeof classes === "string") {
      classes = splitClassName(classes);
    } else if (typeof classes === "object" && classes.baseVal) {
      // SVG 元素
      classes = splitClassName(classes.baseVal);
    } else {
      return;
    }

    for (var i = 0; i < classes.length; i++) {
      var cls = classes[i];
      if (cls && !cache[cls]) {
        insertRule(cls);
      }
    }
  }

  function scanDOM() {
    var all = document.querySelectorAll("[class]");
    var count = 0;
    for (var i = 0; i < all.length; i++) {
      count++;
      processElement(all[i]);
    }
    console.log("[atomic] scanDOM() scanned", count, "elements, cache size:", Object.keys(cache).length, "buffer length:", buffer.length);
  }

  function setupObserver() {
    if (observer) observer.disconnect();
    observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        if (m.type === "childList") {
          for (var j = 0; j < m.addedNodes.length; j++) {
            var node = m.addedNodes[j];
            if (node.nodeType === 1) {
              processElement(node);
              var children = node.querySelectorAll("[class]");
              for (var k = 0; k < children.length; k++) {
                processElement(children[k]);
              }
            }
          }
        } else if (m.type === "attributes" && m.attributeName === "class") {
          processElement(m.target);
        }
      }
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class"],
    });
    console.log("[atomic] observer started, observing:", !!document.documentElement);
  }

  // ==================== 初始化 ====================
  function createStyleEl() {
    var el = document.createElement("style");
    el.id = "__atomic_css__";
    el.setAttribute("data-atomic", "true");
    el.textContent = "/* atomic-css-engine */";
    document.head.appendChild(el);
    console.log("[atomic] style element created, sheet:", !!el.sheet, "hasRules:", el.sheet ? el.sheet.cssRules.length : "N/A");
    return el;
  }

  function init(userConfig) {
    if (userConfig) {
      config = extend({}, DEFAULT_CONFIG, userConfig);
    }

    console.log("[atomic] init(), readyState:", document.readyState, "head:", !!document.head, "body:", !!document.body);
    styleEl = createStyleEl();
    sheet = styleEl.sheet;
    buffer = "";

    scanDOM();
    setupObserver();
    console.log("[atomic] init() done, styleEl:", !!styleEl, "sheet:", !!sheet, "observer:", !!observer);
  }

  // ==================== 对外 API ====================
  window.__atomicConfig = function (userConfig) {
    cache = {};
    ruleIndex = 0;
    if (styleEl) {
      styleEl.parentNode.removeChild(styleEl);
    }
    if (observer) {
      observer.disconnect();
    }
    init(userConfig);
  };

  // 强制重新扫描所有 DOM（用于 Vue 挂载完成后）
  window.__atomicRescan = function () {
    console.log("[atomic] __atomicRescan() called, current styleEl:", !!styleEl, "inDOM:", !!(styleEl && styleEl.parentNode));
    cache = {};
    buffer = "/* atomic-css-engine */\n";
    if (!styleEl || !styleEl.parentNode) {
      styleEl = createStyleEl();
    }
    styleEl.textContent = buffer;
    sheet = styleEl.sheet;
    if (observer) {
      observer.disconnect();
    }
    scanDOM();
    setupObserver();
    console.log("[atomic] __atomicRescan() done, cache size:", Object.keys(cache).length, "buffer length:", buffer.length);
  };

  // 自动初始化
  window.__ATOMIC_DEBUG__ = true;

  (function () {
    console.log("[atomic] auto-init start, readyState:", document.readyState, "head:", !!document.head, "body:", !!document.body);

    function start() {
      console.log("[atomic] start() executing...");
      styleEl = createStyleEl();
      sheet = styleEl.sheet;
      buffer = "";
      scanDOM();
      setupObserver();
      console.log("[atomic] auto-init complete");
    }

    if (document.head) {
      if (document.body) {
        start();
      } else {
        document.addEventListener("DOMContentLoaded", start, { once: true });
      }
    } else {
      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", start, { once: true });
      } else {
        setTimeout(start, 0);
      }
    }
  })();
})();